import { TEST_IDS } from '@va/constants';
import { CloseButton, useTooltipContext } from '@va/ui/tooltips';
import { appHasDarkTheme } from '@va/util/helpers';
import { useWindowDimensions } from '@va/util/hooks';
import classNames from 'classnames';
import { useMemo } from 'react';
import { TooltipSubtitle } from '../common/TooltipSubtitle';
import { TooltipTitle } from '../common/TooltipTitle';
import { BasicTooltipGridRow } from './BasicTooltipGridRow';

export type RawTooltipRow = {
  value: number;
  color: string;
  linkTo?: string;
};
export type BasicChartTooltipValues = Record<string, RawTooltipRow>;

export type BasicChartTooltipProps = {
  currentValues: BasicChartTooltipValues;
  title?: string;
  subtitle?: string;
  closeTooltip?: () => void;
  valueTransformer?: (val: any) => any;
  labelExtras?: Record<string, string>;
};

export const BasicChartTooltipComponent = ({
  currentValues,
  subtitle,
  title,
  closeTooltip,
  valueTransformer,
}: BasicChartTooltipProps) => {
  const { isMobile } = useWindowDimensions();
  const isDarkThemeApplied = appHasDarkTheme();
  const { context } = useTooltipContext();

  const lineKeys = useMemo(
    () => Object.keys(currentValues).sort((a, b) => currentValues[b].value - currentValues[a].value),
    [currentValues],
  );

  const gridStyle = useMemo(() => {
    if (isMobile) return '125px minmax(0px, 60px)';
    return 'auto minmax(0, 100px)';
  }, [isMobile]);

  return (
    <div
      data-testid={TEST_IDS.generic.chart.tooltipContainer}
      className={classNames('text-12 sm-initial:text-15 pb-6px', {
        'text-white': !isDarkThemeApplied,
      })}
    >
      <div className='flex justify-between gap-2'>
        <TooltipTitle title={title} />
        <CloseButton
          onClose={() => [context.onOpenChange(false), closeTooltip?.()]}
          className={classNames({
            'top-2': !title,
          })}
        />
      </div>
      <div
        className={classNames('pt-2 grid gap-x-15px gap-y-3 sm-initial:items-center', {})}
        style={{
          gridTemplateColumns: gridStyle,
        }}
      >
        {/* Header */}
        <TooltipSubtitle subtitle={subtitle} />
        <div />
        {lineKeys.map((key) => (
          <BasicTooltipGridRow
            data-testid={TEST_IDS.helpers.createListItemId(key)}
            key={key}
            rowKey={key}
            currentValue={currentValues[key]}
            valueTransformer={valueTransformer}
          />
        ))}
      </div>
    </div>
  );
};
